import { Button, type ButtonProps } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { useT } from '~/i18n/use-t'
import type { FaqId } from './faq-ids'
import { useHelp } from './layout'

/** Opens the Help sheet on one FAQ entry: `useHelp().open({ faq })` (design spec 2026-10-30 §4.1). */
export function useOpenFaq(): (faq: FaqId) => void {
  const help = useHelp()
  return (faq) => help.open({ faq })
}

/**
 * The one underlined text button of the member pages (48 x 48 px hit area, 16 px text, muted): "Why?" next to the
 * thing it explains, and every other text action ("No code?", "Camera help", "I wrote the wrong email"...).
 *
 * - `faq`: opens the Help sheet on that entry; no `faq` and no `onClick` opens the sheet with nothing expanded.
 * - `onClick`: any other action (it replaces the sheet).
 * - `inline`: at the end of a sentence, inside its `<Text>`. The 48 px hit box overhangs the line (negative block
 *   margins), so the line stays as tall as the text around it.
 */
export function WhyLink({
  faq,
  label,
  inline = false,
  onClick,
  ...rest
}: { faq?: FaqId; label?: ReactNode; inline?: boolean } & Omit<ButtonProps, 'children'>) {
  const { t } = useT()
  const help = useHelp()
  return (
    <Button
      // Marks the one text button of the member pages (tests find it by this).
      data-text-button=''
      type='button'
      variant='plain'
      minH='12'
      minW='12'
      h='auto'
      px={0}
      fontSize='md'
      fontWeight='normal'
      lineHeight='1.5'
      color='fg.muted'
      textDecoration='underline'
      textUnderlineOffset='2px'
      whiteSpace='normal'
      textAlign='start'
      justifyContent='flex-start'
      css={
        inline
          ? { display: 'inline-flex', verticalAlign: 'middle', marginBlock: '-0.75rem', marginInlineStart: '0.25rem' }
          : undefined
      }
      onClick={onClick ?? (() => (faq ? help.open({ faq }) : help.open()))}
      {...rest}
    >
      {label ?? t('common.why')}
    </Button>
  )
}
